import { useState, useEffect, useRef } from "react";
import "./App.css";
import {
  Car, MapPin, Users, Tag, Lock, Settings, Briefcase, UserCheck,
  LayoutDashboard, ClipboardList, ChevronLeft, Eye, MessageSquare,
  LogOut, Bell, Building2, Handshake,
  List, Info, DollarSign, User as UserIcon, Gift, ShieldAlert, FileText, CreditCard
} from "lucide-react";

// Build info for version tracking
const BUILD_VERSION = {
  gitSha: "RESELLER-DEALER-MGMT-20260127-1530",
  buildTime: new Date().toISOString(),
  version: "1.4.0-reseller-dealer-mgmt",
  buildId: "RESELLER-DEALER-MGMT-20260127-1530",
};

// Phase 8A Context Refactor - Import providers from dedicated modules
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ToastProvider, useToast } from "./contexts/ToastContext";
import { Logo } from "./components/Logo";
import { NotificationBell } from "./components/NotificationBell";

// Phase 7B-7J Frontend Refactor - Import extracted pages
import GeoDemandPage from "./pages/GeoDemandPage";
import OverviewPage from "./pages/OverviewPage";
import VehicleDemandPage from "./pages/VehicleDemandPage";
import MasterAdminDashboardPage from "./pages/MasterAdminDashboardPage";
import MarketingDemandPage from "./pages/MarketingDemandPage";
import DemandHealthPage from "./pages/DemandHealthPage";
import SettingsPage from "./pages/SettingsPage";
import AccountSettingsPage from "./pages/AccountSettingsPage";
import OffersPage from "./pages/OffersPage";
import LeadsPage from "./pages/LeadsPage";
import IdentifiedVisitorsPage from "./pages/IdentifiedVisitorsPage";
import UnlockSettingsPage from "./pages/UnlockSettingsPage";
import SmartReturnOffersPage from "./pages/SmartReturnOffersPage";  // P2-A
import SubMasterDashboard from "./pages/SubMasterDashboard";  // P1-E
import ConquestAuditCreditsPage from "./pages/ConquestAuditCreditsPage";
// Dealer Dashboard
import DealerDashboardPage from "./pages/DealerDashboardPage";
import LeadDetailPage from "./pages/LeadDetailPage";

// Phase 9 — Nav IA: new dealer-facing pages
import PopularTrimsPage from "./pages/PopularTrimsPage";
import PopularStocksPage from "./pages/PopularStocksPage";
import GeneralInfoPage from "./pages/GeneralInfoPage";
import TestDrivePage from "./pages/TestDrivePage";
import TestDriveBookPage from "./pages/TestDriveBookPage";
import DealerRedemptionPage from "./pages/dealer/DealerRedemptionPage";
import DealerRedemptionLog from "./pages/dealer/DealerRedemptionLog";
import SendLogPage from "./pages/admin/SendLogPage";
import PricingRulesPage from "./pages/PricingRulesPage";

// Phase 2E: Admin pages
import { AdminDealersPage, AdminDealerNewPage, AdminDealerDetailPage } from "./pages/admin";
import AuditLogPage from "./pages/admin/AuditLogPage";
import SmsCountersPage from "./pages/admin/SmsCountersPage";
import ResellerManagementPage from "./pages/admin/ResellerManagementPage";
import AdminResellerDetailPage from "./pages/admin/AdminResellerDetailPage";
import DealerGroupsPage from "./pages/admin/DealerGroupsPage";

// Multi-Rooftop: rooftop switcher dropdown shown in TopNav for users with >1 rooftop
import RooftopSwitcher from "./components/layout/RooftopSwitcher";

// Accept Invite Page (for invited users only)
import AcceptInvitePage from "./pages/AcceptInvitePage";
import RecallDealerPage from "./recall/RecallDealerPage";
import RecallStandalonePage from "./recall/RecallStandalonePage";
import BrochurePage from "./brochure/BrochurePage";
import BrochureBuilderPage from "./brochure/BrochureBuilderPage";
import BrochureNetworkSummaryPage from "./brochure/BrochureNetworkSummaryPage";

// ============== Auth Components ==============
const LoginForm = ({ onForgotPassword }) => {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 24 }}>
          <Logo variant="light" height={48} />
        </div>
        <h2>Welcome back</h2>
        <p className="auth-subtitle">Sign in to your account</p>
        {error && <div className="error-message">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Email address</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@example.com" data-testid="login-email" />
          </div>
          <div className="form-group">
            <div className="label-row">
              <label>Password</label>
              <button type="button" className="link-button" onClick={onForgotPassword}>Forgot password?</button>
            </div>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••" data-testid="login-password" />
          </div>
          <button type="submit" className="btn-primary" disabled={isLoading} data-testid="login-submit">
            {isLoading ? "Signing in..." : "Sign in"}
          </button>
        </form>
        <p className="auth-footer" style={{ color: '#9ca3af', fontSize: '13px' }}>
          Need an account? Contact your administrator for an invitation.
        </p>
      </div>
    </div>
  );
};

const RegisterForm = ({ onSwitchToLogin }) => {
  const { register } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);
    try {
      await register(email, password, firstName, lastName);
    } catch (err) {
      setError(err.response?.data?.detail || "Registration failed");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 24 }}>
          <Logo variant="light" height={48} />
        </div>
        <h2>Create account</h2>
        <p className="auth-subtitle">Sign up to get started</p>
        {error && <div className="error-message">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>First name</label>
              <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="John" data-testid="register-firstname" />
            </div>
            <div className="form-group">
              <label>Last name</label>
              <input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Doe" data-testid="register-lastname" />
            </div>
          </div>
          <div className="form-group">
            <label>Email address</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@example.com" data-testid="register-email" />
          </div>
          <div className="form-group">
            <label>Password</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} placeholder="At least 8 characters" data-testid="register-password" />
          </div>
          <button type="submit" className="btn-primary" disabled={isLoading} data-testid="register-submit">
            {isLoading ? "Creating account..." : "Create account"}
          </button>
        </form>
        <p className="auth-footer">
          Already have an account? <button type="button" className="link-button" onClick={onSwitchToLogin}>Sign in</button>
        </p>
      </div>
    </div>
  );
};

const ForgotPasswordForm = ({ onBackToLogin }) => {
  const { resetPassword } = useAuth();
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const result = await resetPassword(email);
      setMessage(result.message);
    } catch (err) {
      setMessage("Error sending reset email");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h2>Reset password</h2>
        <p className="auth-subtitle">Enter your email to receive a reset link</p>
        {message && <div className="success-message">{message}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Email address</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@example.com" />
          </div>
          <button type="submit" className="btn-primary" disabled={isLoading}>
            {isLoading ? "Sending..." : "Send reset link"}
          </button>
        </form>
        <p className="auth-footer">
          <button type="button" className="link-button" onClick={onBackToLogin}>← Back to sign in</button>
        </p>
      </div>
    </div>
  );
};

// ============== TopNav Component ==============
const TopNav = ({ dealerName, userEmail, onSettingsClick, onAccountSettings, isMasterAdmin, onNavigate }) => {
  const { logout, user } = useAuth();
  const initial = (userEmail || 'U').charAt(0).toUpperCase();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    const onEsc = (e) => { if (e.key === "Escape") setMenuOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [menuOpen]);

  const displayName = [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim();

  return (
    <div className="top-nav" data-testid="top-nav">
      <div className="top-nav-left">
        <span className="top-nav-dealer" data-testid="dealer-name">{dealerName || 'Verified Demand'}</span>
        <RooftopSwitcher />
      </div>
      <div className="top-nav-right">
        {/* iter82 — Bug 12: Search button removed until search is built.
            Reinstate by re-adding the line below with an actual onClick. */}
        {/* <button className="top-nav-icon-btn" title="Search"><Search size={18} /></button> */}
        {/* iter132g — gear icon removed from TopNav. The Settings page
            now lives as its own sidebar tab (Settings → Settings) so
            dealers don't miss it. Account-level settings still live
            inside the avatar dropdown below. */}
        {/* <button className="top-nav-icon-btn" title="Settings" onClick={onSettingsClick}><Settings size={18} /></button> */}
        {isMasterAdmin && <NotificationBell onNavigate={onNavigate} />}
        <div className="top-nav-avatar-wrapper" ref={menuRef}>
          <button
            type="button"
            className="top-nav-avatar"
            title={userEmail}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((o) => !o)}
            data-testid="topnav-avatar-btn"
          >
            {initial}
          </button>
          {menuOpen && (
            <div className="top-nav-user-menu" role="menu" data-testid="topnav-user-menu">
              <div className="top-nav-user-menu-header" data-testid="topnav-user-identity">
                {displayName && <div className="top-nav-user-menu-name">{displayName}</div>}
                <div className="top-nav-user-menu-email">{userEmail || user?.email || "—"}</div>
              </div>
              {/* iter90 — Issue 13: Account Settings is now reachable from
                  the avatar dropdown, alongside Sign Out. */}
              <button
                type="button"
                className="top-nav-user-menu-item"
                onClick={() => { setMenuOpen(false); onAccountSettings?.(); }}
                data-testid="topnav-account-settings-btn"
                role="menuitem"
              >
                <UserIcon size={14} /> Account Settings
              </button>
              <button
                type="button"
                className="top-nav-user-menu-item top-nav-user-menu-signout"
                onClick={() => { setMenuOpen(false); logout(); }}
                data-testid="topnav-signout-btn"
                role="menuitem"
              >
                <LogOut size={14} /> Sign Out
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============== Dashboard Components ==============
const Sidebar = ({ currentPage, setCurrentPage }) => {
  const { logout, user, viewingDealerId, isReadOnly, switchDealer } = useAuth();

  const isDealer = user?.role === "DEALER_ADMIN" || user?.role === "DEALER_USER";
  const isMasterAdmin = user?.role === "MASTER_ADMIN";
  const isSubMaster = user?.role === "SUB_MASTER";
  const canAccessUnlockOffer = isMasterAdmin || user?.dealer_unlock_control_enabled;

  // Build grouped nav sections
  const sections = [];

  if (isSubMaster) {
    // Reseller (SUB_MASTER) ONLY ever sees their aggregate dashboard.
    // No drill-in, no dealer-private nav items — the restriction middleware
    // also enforces this at the API level.
    sections.push({
      label: null,
      items: [
        { id: "submaster-dashboard", label: "My Dealers", icon: <Briefcase /> },
        { id: "conquest-audit-credits", label: "Conquest Audit Credits", icon: <CreditCard /> },
        // Coming soon — mirrors the Master Admin section for now, "for show"
        // only per explicit request. Grayed-out/disabled, no backend wiring.
        { id: "reseller-dashboard-cs", label: "Dashboard", icon: <LayoutDashboard />, disabled: true, disabledReason: "Coming soon" },
        { id: "reseller-dealer-mgmt-cs", label: "Dealer Management", icon: <Building2 />, disabled: true, disabledReason: "Coming soon" },
        { id: "reseller-dealer-groups-cs", label: "Dealer Groups", icon: <Handshake />, disabled: true, disabledReason: "Coming soon" },
        { id: "reseller-giftcard-log-cs", label: "Gift-card Send Log", icon: <Gift />, disabled: true, disabledReason: "Coming soon" },
        { id: "reseller-brochure-network-cs", label: "Brochure Network", icon: <FileText />, disabled: true, disabledReason: "Coming soon" },
        { id: "reseller-audit-log-cs", label: "Audit Log", icon: <ShieldAlert />, disabled: true, disabledReason: "Coming soon" },
        { id: "reseller-sms-volume-cs", label: "SMS Volume", icon: <MessageSquare />, disabled: true, disabledReason: "Coming soon" },
      ],
    });
  } else {
    // iter139 F — Results-first restructure. The dealer's mental model is:
    // "how many people came into my store because of this?" first, then
    // "where's my demand coming from?", then "what am I configuring?".
    // So the nav groups reflect that priority:
    //   RESULTS         → Dashboard, Showroom Visits, Customer Leads
    //   MARKET INSIGHT  → Geographic Demand, Popular Trims/Stocks
    //   PRODUCTS/SETUP  → Unlock, Pricing Rules, Test Drive, Redeem
    //   SETTINGS        → General Info, Settings
    //
    // Master-admin nav (no LEADS & ANALYTICS section) is unchanged — they
    // still get a single Dashboard entry under Overview.
    if (isMasterAdmin) {
      // Master admin: keep original single-group nav
      sections.push({
        label: "Overview",
        items: [{ id: "vehicle-demand", label: "Dashboard", icon: <LayoutDashboard /> }],
      });
    } else {
      // ── Group 1 — RESULTS (money-first: dashboard, walk-ins, leads) ──
      const resultsItems = [
        { id: "dealer-dashboard", label: "Dashboard", icon: <LayoutDashboard /> },
      ];
      // Showroom Visits (renamed from "Test Drive Leads") is added ONLY if
      // this dealer has Test Drive enabled — same gate as before, just
      // hoisted into the Results group so the highest-value screen is at
      // the top of the nav.
      if (isDealer && user?.dealer_unlock_control_enabled) {
        resultsItems.push({ id: "test-drive-log", label: "Showroom Visits", icon: <ClipboardList /> });
      }
      resultsItems.push({ id: "leads", label: "Customer Leads", icon: <Users /> });
      resultsItems.push({ id: "identified-visitors", label: "Identified Visitors", icon: <UserCheck /> });
      sections.push({ label: "Results", items: resultsItems });

      // ── Group 2 — MARKET INSIGHT (where's demand coming from?) ──
      sections.push({
        label: "Market Insight",
        items: [
          { id: "geo-demand", label: "Geographic Demand", icon: <MapPin /> },
          { id: "popular-trims", label: "Popular Trims", icon: <Tag /> },
          { id: "popular-stocks", label: "Popular Stocks", icon: <List /> },
        ],
      });

      // ── Group 3 — PRODUCTS / SETUP ──
      if (user?.dealer_unlock_control_enabled) {
        const productsItems = [
          { id: "unlock-settings", label: "Unlock Price", icon: <Lock /> },
        ];
        if (user?.franchise_make !== "USED_CARS_ONLY") {
          productsItems.push({
            id: "pricing-rules-new",
            label: "Pricing Rules (New)",
            icon: <DollarSign />,
            disabled: !user?.franchise_make,
            disabledReason: "Set a Franchise Make on General Info to configure New Vehicle pricing.",
          });
        }
        productsItems.push({ id: "pricing-rules-used", label: "Pricing Rules (Used)", icon: <DollarSign /> });
        productsItems.push({ id: "test-drive", label: "Test Drive", icon: <Car /> });
        // Dealer redemption page — where staff type a claim ticket to
        // trigger an SMS-only gift-card send.
        if (isDealer) {
          productsItems.push({ id: "redeem", label: "Redeem Gift Card", icon: <Gift /> });
        }
        sections.push({ label: "Products / Setup", items: productsItems });
      }

      // Recall Check — à la carte add-on with its own entitlement flag, so
      // it appears independently of the unlock-control gate above.
      if (user?.recall_check_enabled) {
        sections.push({
          label: "Recall Check",
          items: [{ id: "recall-check", label: "Recall Check", icon: <ShieldAlert /> }],
        });
      }

      // Brochure Builder — à la carte add-on with its own entitlement flag,
      // hydrated by /api/me from dealer_entitlements.
      if (user?.brochure_builder_enabled) {
        sections.push({
          label: "Brochure Builder",
          items: [{ id: "brochure-builder", label: "Brochure Builder", icon: <FileText /> }],
        });
      }


      // ── Group 4 — SETTINGS ──
      sections.push({
        label: "Settings",
        items: [
          { id: "general-info", label: "General Info", icon: <Info /> },
          { id: "settings", label: "Settings", icon: <Settings /> },
        ],
      });
    }
  }

  // ADMIN section (master admin only) — separator above
  if (isMasterAdmin) {
    sections.push({
      label: "Admin",
      admin: true,
      items: [
        { id: "admin-dealers", label: "Dealer Management", icon: <Building2 /> },
        { id: "admin-resellers", label: "Reseller Management", icon: <Handshake /> },
        { id: "admin-dealer-groups", label: "Dealer Groups", icon: <Briefcase /> },
        { id: "admin-send-log", label: "Gift-card Send Log", icon: <Gift /> },
        { id: "admin-brochure-network", label: "Brochure Network", icon: <FileText /> },
        { id: "audit-log", label: "Audit Log", icon: <ClipboardList /> },
        { id: "sms-counters", label: "SMS Volume", icon: <MessageSquare /> },
      ],
    });
  }

  const isAdminPage = ["admin-dealers", "admin-resellers", "audit-log", "sms-counters", "admin-dealer-new", "admin-dealer-detail", "admin-reseller-detail", "admin-dealer-groups"].includes(currentPage);
  const initial = (user?.email || 'U').charAt(0).toUpperCase();

  const isItemActive = (itemId) => {
    if (currentPage === itemId) return true;
    if (itemId === "admin-dealers" && isAdminPage && currentPage.startsWith("admin-dealer")) return true;
    return false;
  };

  return (
    <aside className="sidebar" data-testid="sidebar">
      {/* Logo + brand */}
      <div className="sidebar-logo-mark">
        {/* iter107 — Real brand logo replaces purple diamond placeholder.
            Variant "dark" because the sidebar background is Deep Navy. */}
        <Logo variant="dark" height={32} />
      </div>

      <nav className="sidebar-nav">
        {sections.map((section, sIdx) => (
          <div key={`section-${sIdx}`}>
            {section.admin && <div className="nav-section-admin-divider" />}
            {section.label && (
              <div className="nav-section-label" data-testid={`nav-section-${section.label.toLowerCase().replace(/\s|&/g, '-').replace(/--+/g, '-')}`}>
                {section.label}
              </div>
            )}
            {section.items.map(item => (
              <button
                key={item.id}
                className={`nav-item ${isItemActive(item.id) ? "active" : ""} ${item.disabled ? "is-disabled" : ""}`}
                onClick={() => !item.disabled && setCurrentPage(item.id)}
                disabled={item.disabled}
                title={item.disabled ? item.disabledReason : undefined}
                data-testid={`nav-${item.id}`}
              >
                <span className="nav-icon">{item.icon}</span>
                <span className="nav-label">{item.label}</span>
                {item.disabled && <span className="ml-auto text-amber-500 text-xs" aria-hidden="true">⚠</span>}
              </button>
            ))}
          </div>
        ))}
      </nav>

      <div className="sidebar-footer">
        <div className="sidebar-user-avatar" title={user?.email}>{initial}</div>
        <button onClick={logout} className="logout-btn" data-testid="logout-button" title="Sign out">
          <LogOut size={15} />
        </button>
      </div>
    </aside>
  );
};

const Dashboard = () => {
  const { user, viewingDealerId, apiFetch } = useAuth();
  const isMasterAdmin = user?.role === "MASTER_ADMIN";
  const [dealerName, setDealerName] = useState('');

  // Fetch dealer/business name for top nav
  useEffect(() => {
    apiFetch('/settings/business', { method: 'GET' })
      .then(res => setDealerName(res.data?.name || ''))
      .catch(() => {});
  }, [apiFetch]);
  
  // Phase 2E-P0: URL-based navigation with query params for deep linking
  const getInitialState = () => {
    const params = new URLSearchParams(window.location.search);
    let page = params.get('page') || 'overview';
    
    // P1-E: Default sub-masters to their dashboard
    if (!params.get('page') && user?.role === 'SUB_MASTER') {
      page = 'submaster-dashboard';
    }
    
    // Default DEALER_ADMIN to the Geographic Demand (Lead Performance & Geo Demand) page
    if (!params.get('page') && user?.role === 'DEALER_ADMIN') {
      page = 'geo-demand';
    }

    // MASTER_ADMIN: 'overview' (DemandHealthPage) is dealer-facing — it shows
    // Top Vehicles and per-site demand. Route master admins to the network
    // overview page (vehicle-demand → MasterAdminDashboardPage) instead.
    // Also catches the post-login fallback where ?page=login briefly persists.
    if (user?.role === 'MASTER_ADMIN' && (page === 'overview' || page === 'login')) {
      page = 'vehicle-demand';
    }
    
    // SECURITY: Prevent non-admins from landing on admin pages via URL manipulation
    const adminPages = ['admin-dealers', 'admin-dealer-new', 'admin-dealer-detail', 'audit-log', 'sms-counters', 'admin-resellers', 'admin-reseller-detail', 'admin-dealer-groups'];
    if (adminPages.includes(page) && user?.role !== 'MASTER_ADMIN') {
      console.log('[SECURITY] Non-admin attempted to access admin page, redirecting to overview');
      page = user?.role === 'SUB_MASTER' ? 'submaster-dashboard' : 'geo-demand';
    }

    // SECURITY: SUB_MASTER (reseller) is confined to their aggregate dashboard.
    // They cannot access dealer-private pages via URL manipulation. The backend
    // restriction middleware (auth/submaster_guard.py) enforces this at the API
    // layer too.
    if (user?.role === 'SUB_MASTER' && page !== 'submaster-dashboard' && page !== 'conquest-audit-credits') {
      console.log('[SECURITY] Reseller attempted to access dealer page, redirecting to submaster dashboard');
      page = 'submaster-dashboard';
    }
    
    const dealerId = params.get('dealer');
    const leadId = params.get('lead');
    const tab = params.get('tab') || 'overview';
    return { page, dealerId, leadId, tab };
  };
  
  const initialState = getInitialState();
  const [currentPage, setCurrentPage] = useState(initialState.page);
  const [adminDealerId, setAdminDealerId] = useState(initialState.dealerId);
  const [selectedLeadId, setSelectedLeadId] = useState(initialState.leadId);
  const [adminDealerTab, setAdminDealerTab] = useState(initialState.tab);
  const [leadsTab, setLeadsTab] = useState("all"); // Track leads page tab
  
  // P1-E: Redirect sub-masters to their dashboard on login
  useEffect(() => {
    if (user?.role === 'SUB_MASTER' && currentPage === 'overview' && !viewingDealerId) {
      setCurrentPage('submaster-dashboard');
    }
    // Master admin should never land on the dealer-facing 'overview' page —
    // route them to the network dashboard. Also catch 'login' (briefly persists
    // post-auth) so the master never sees OverviewPage as a fallback.
    if (user?.role === 'MASTER_ADMIN' && (currentPage === 'overview' || currentPage === 'login')) {
      setCurrentPage('vehicle-demand');
    }
  }, [user, currentPage, viewingDealerId]);

  // iter88 — Issue 23: dynamic browser tab title. Helps dealers who
  // work with multiple Verified Demand tabs identify the right one.
  // Map currentPage → human title; falls back to plain "Verified
  // Demand" for routes we don't have a specific name for.
  useEffect(() => {
    const PAGE_TITLES = {
      "dealer-dashboard": "Dashboard",
      "submaster-dashboard": "Reseller Dashboard",
      "conquest-audit-credits": "Conquest Audit Credits",
      "vehicle-demand": "Network Dashboard",
      "geo-demand": "Geographic Demand",
      "leads": "Customer Leads",
      "identified-visitors": "Identified Visitors",
      "lead-detail": "Lead Detail",
      "popular-trims": "Popular Trims",
      "popular-stocks": "Popular Stocks",
      "pricing-rules-new": "Pricing Rules (New)",
      "pricing-rules-used": "Pricing Rules (Used)",
      "unlock-settings": "Unlock Price",
      "general-info": "General Info",
      "settings": "Settings",
      "audit-log": "Audit Log",
      "sms-counters": "SMS Volume",
      "admin-dealers": "Dealer Management",
      "admin-resellers": "Reseller Management",
      "admin-dealer-new": "New Dealer",
      "admin-dealer-detail": "Dealer Detail",
      "admin-reseller-detail": "Reseller Detail",
      "admin-dealer-groups": "Dealer Groups",
      "account-settings": "Account Settings",
      "brochure-builder": "Brochure Builder",
      "admin-brochure-network": "Brochure Network",
      "login": "Sign In",
    };
    const label = PAGE_TITLES[currentPage];
    document.title = label ? `${label} — Verified Demand` : "Verified Demand";
  }, [currentPage]);

  // Update URL when navigation changes
  const updateUrl = (page, dealerId = null, tab = null, leadId = null, leadsTabVal = null) => {
    const params = new URLSearchParams();
    params.set('page', page);
    if (dealerId) params.set('dealer', dealerId);
    if (leadId) params.set('lead', leadId);
    if (tab && page === 'admin-dealer-detail') params.set('tab', tab);
    if (leadsTabVal && (page === 'leads' || page === 'lead-detail')) params.set('leadsTab', leadsTabVal);
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.pushState({page, dealerId, tab, leadId, leadsTab: leadsTabVal}, '', newUrl);
  };

  // Handle browser back/forward
  useEffect(() => {
    const handlePopState = (event) => {
      if (event.state) {
        setCurrentPage(event.state.page || 'overview');
        setAdminDealerId(event.state.dealerId || null);
        setSelectedLeadId(event.state.leadId || null);
        setAdminDealerTab(event.state.tab || 'overview');
        setLeadsTab(event.state.leadsTab || 'all');
      } else {
        // Inline getInitialState logic to avoid dependency warning
        const params = new URLSearchParams(window.location.search);
        const page = params.get('page') || 'overview';
        const dealerId = params.get('dealer');
        const leadId = params.get('lead');
        const tab = params.get('tab') || 'overview';
        setCurrentPage(page);
        setAdminDealerId(dealerId);
        setSelectedLeadId(leadId);
        setAdminDealerTab(tab);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Navigation helper for admin pages
  const handleNavigate = (page, id = null, tab = null, leadId = null) => {
    // SECURITY: Reseller (SUB_MASTER) is confined to submaster-dashboard.
    // Any other nav attempt is redirected.
    if (user?.role === 'SUB_MASTER' && page !== 'submaster-dashboard' && page !== 'conquest-audit-credits') {
      page = 'submaster-dashboard';
      id = null; tab = null; leadId = null;
    }
    setCurrentPage(page);
    if (id) setAdminDealerId(id);
    if (leadId) setSelectedLeadId(leadId);
    if (tab) setAdminDealerTab(tab);
    updateUrl(page, id, tab, leadId, leadsTab);
  };

  // Navigate to lead detail - preserves current leads tab
  const handleNavigateToLead = (leadId, fromTab = null) => {
    if (fromTab) setLeadsTab(fromTab);
    setSelectedLeadId(leadId);
    setCurrentPage('lead-detail');
    updateUrl('lead-detail', null, null, leadId, fromTab || leadsTab);
  };

  // Navigate back to leads page - restore tab
  const handleNavigateBackToLeads = () => {
    setCurrentPage('leads');
    updateUrl('leads', null, null, null, leadsTab);
  };

  // Handle tab change from dealer detail page
  const handleTabChange = (tab) => {
    setAdminDealerTab(tab);
    updateUrl(currentPage, adminDealerId, tab);
  };

  const renderPage = () => {
    switch (currentPage) {
      case "overview": return <OverviewPage />;
      case "dealer-dashboard": return <DealerDashboardPage onNavigateToLead={handleNavigateToLead} />;
      case "demand-health": return <DemandHealthPage />;
      case "vehicle-demand":
        // Master admin sees a network-overview dashboard; resellers and other
        // non-dealer roles continue to see vehicle-level demand.
        return isMasterAdmin
          ? <MasterAdminDashboardPage onNavigate={handleNavigate} />
          : <VehicleDemandPage />;
      case "geo-demand": return <GeoDemandPage onNavigate={handleNavigate} />;
      case "marketing-demand": return <MarketingDemandPage />;
      case "offers": return <OffersPage />;
      case "leads": return <LeadsPage onNavigateToLead={(leadId) => handleNavigateToLead(leadId)} initialTab={leadsTab} onTabChange={setLeadsTab} />;
      case "identified-visitors": return <IdentifiedVisitorsPage />;
      case "lead-detail": return (
        <LeadDetailPage 
          leadId={selectedLeadId} 
          onNavigateBack={handleNavigateBackToLeads} 
        />
      );
      case "unlock-settings": return <UnlockSettingsPage onNavigate={handleNavigate} />;
      case "test-drive": return <TestDrivePage />;
      case "redeem": return <DealerRedemptionPage />;
      case "test-drive-log": return <DealerRedemptionLog />;
      case "recall-check": return <RecallDealerPage />;
      case "brochure-builder": return <BrochureBuilderPage />;
      case "admin-brochure-network":
        if (!isMasterAdmin) return <OverviewPage />;
        return <BrochureNetworkSummaryPage onNavigate={handleNavigate} />;
      case "admin-send-log": return <SendLogPage />;
      case "popular-trims": return <PopularTrimsPage />;
      case "popular-stocks": return <PopularStocksPage />;
      case "general-info": return <GeneralInfoPage />;
      case "pricing-rules-new": return <PricingRulesPage condition="new" />;
      case "pricing-rules-used": return <PricingRulesPage condition="used" />;
      case "smart-return-offers": return <SmartReturnOffersPage />;  // P2-A
      case "submaster-dashboard": return <SubMasterDashboard />;  // P1-E
      case "conquest-audit-credits": return <ConquestAuditCreditsPage />;
      case "settings": return <SettingsPage onNavigate={handleNavigate} />;
      case "account-settings": return <AccountSettingsPage />;
      // Phase 2E: Admin pages with URL params (MASTER_ADMIN only)
      case "admin-dealers": 
        if (!isMasterAdmin) return <OverviewPage />;
        return <AdminDealersPage key="admin-dealers" onNavigate={handleNavigate} />;
      case "admin-dealer-new": 
        if (!isMasterAdmin) return <OverviewPage />;
        return <AdminDealerNewPage onNavigate={handleNavigate} />;
      case "admin-dealer-detail": 
        if (!isMasterAdmin) return <OverviewPage />;
        return (
          <AdminDealerDetailPage 
            dealerId={adminDealerId} 
            onNavigate={handleNavigate}
            initialTab={adminDealerTab}
            onTabChange={handleTabChange}
          />
        );
      case "audit-log": 
        if (!isMasterAdmin) return <OverviewPage />;
        return <AuditLogPage onNavigate={handleNavigate} />;
      case "sms-counters":
        if (!isMasterAdmin) return <OverviewPage />;
        return <SmsCountersPage />;
      case "admin-resellers": 
        if (!isMasterAdmin) return <OverviewPage />;
        return <ResellerManagementPage key="admin-resellers" onNavigate={handleNavigate} />;
      case "admin-reseller-detail": 
        if (!isMasterAdmin) return <OverviewPage />;
        return <AdminResellerDetailPage resellerId={adminDealerId} onNavigate={handleNavigate} />;
      case "admin-dealer-groups":
        if (!isMasterAdmin) return <OverviewPage />;
        return <DealerGroupsPage />;
      default:
        // Defense in depth: if currentPage is unknown (e.g., 'login' immediately
        // post-auth), don't drop master admins onto the dealer-facing OverviewPage.
        if (isMasterAdmin) return <MasterAdminDashboardPage onNavigate={handleNavigate} />;
        return <OverviewPage />;
    }
  };

  return (
    <div className="dashboard-layout" data-testid="dashboard">
      <TopNav
        dealerName={dealerName}
        userEmail={user?.email}
        onSettingsClick={() => handleNavigate("settings")}
        onAccountSettings={() => handleNavigate("account-settings")}
        isMasterAdmin={isMasterAdmin}
        onNavigate={handleNavigate}
      />
      <div className="dashboard-body">
        <Sidebar currentPage={currentPage} setCurrentPage={(page) => handleNavigate(page)} />
        <main className="main-content">
          {renderPage()}
        </main>
      </div>
    </div>
  );
};

// ============== Main App ==============
function App() {
  const [view, setView] = useState("login");

  return (
    <ToastProvider>
      <AuthProvider>
        <AppContent view={view} setView={setView} />
      </AuthProvider>
    </ToastProvider>
  );
}

function AppContent({ view, setView }) {
  const { user, loading } = useAuth();
  const [inviteToken, setInviteToken] = useState(null);
  const [inviteSuccess, setInviteSuccess] = useState(false);

  // Check for accept-invite token in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const path = window.location.pathname;
    
    if (path === '/accept-invite' && token) {
      setInviteToken(token);
    }
  }, []);

  // iter133b — Public booking route. Rendered BEFORE the auth wall so
  // any visitor can book a test drive without a dealer login. Placed
  // AFTER all hooks so hook order stays consistent across renders.
  const isTestDriveBook = typeof window !== "undefined" && window.location.pathname === "/test-drive/book";
  if (isTestDriveBook) return <TestDriveBookPage />;

  // Recall Check — public, unauthenticated route namespace. Rendered before
  // the auth wall so /recall/<slug> never inherits admin session context.
  const isRecallPage = typeof window !== "undefined" && window.location.pathname.startsWith("/recall/");
  if (isRecallPage) return <RecallStandalonePage />;

  // Brochure Builder — public, unauthenticated. Rendered before the auth
  // wall so a customer opening a link from SMS never sees the login page.
  const isBrochurePage = typeof window !== "undefined" && window.location.pathname.startsWith("/brochure/");
  if (isBrochurePage) return <BrochurePage />;

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // If user is already logged in, show dashboard
  if (user) return <Dashboard />;

  // Show invite success message
  if (inviteSuccess) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <h2 style={{ color: '#059669' }}>Account Created!</h2>
          <p className="auth-subtitle">
            Your account has been set up successfully. You can now sign in.
          </p>
          <button 
            className="btn-primary" 
            onClick={() => {
              setInviteSuccess(false);
              setInviteToken(null);
              // Clear URL params
              window.history.pushState({}, '', '/');
            }}
            style={{ marginTop: '16px' }}
          >
            Go to Sign In
          </button>
        </div>
      </div>
    );
  }

  // Show accept invite page if token is present
  if (inviteToken) {
    return (
      <AcceptInvitePage 
        token={inviteToken}
        onSuccess={() => {
          setInviteSuccess(true);
        }}
        onError={(err) => {
          console.error('Invite error:', err);
        }}
      />
    );
  }

  // Standard auth views (login and forgot password only - NO registration)
  return (
    <div className="App">
      {view === "login" && <LoginForm onForgotPassword={() => setView("forgot")} />}
      {view === "forgot" && <ForgotPasswordForm onBackToLogin={() => setView("login")} />}
    </div>
  );
}

export default App;
